import { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  CreditCard,
  FileText,
  MessageSquare,
  LayoutDashboard,
  Zap,
  DollarSign,
  BarChart3,
  Mail,
  Megaphone,
  ClipboardList,
  Store,
} from 'lucide-react';

interface AdminLayoutProps {
  children: ReactNode;
}

interface NavItem {
  id: string;
  label: string;
  icon: ReactNode;
  path: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard className="h-5 w-5" />, path: '/admin' },
  { id: 'skills', label: 'Curate Skills', icon: <Store className="h-5 w-5" />, path: '/admin/skills' },
  { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="h-5 w-5" />, path: '/admin/analytics' },
  { id: 'users', label: 'Users', icon: <Users className="h-5 w-5" />, path: '/admin/users' },
  { id: 'waitlist', label: 'Waitlist', icon: <ClipboardList className="h-5 w-5" />, path: '/admin/waitlist' },
  { id: 'pricing', label: 'Pricing Plans', icon: <DollarSign className="h-5 w-5" />, path: '/admin/pricing' },
  { id: 'models', label: 'AI Models', icon: <Zap className="h-5 w-5" />, path: '/admin/settings/models' },
  { id: 'email-templates', label: 'Email Templates', icon: <Mail className="h-5 w-5" />, path: '/admin/email-templates' },
  { id: 'release-notes', label: 'Release Notes', icon: <Megaphone className="h-5 w-5" />, path: '/admin/release-notes' },
  { id: 'prompts', label: 'Prompt Templates', icon: <MessageSquare className="h-5 w-5" />, path: '/admin/prompts' },
  { id: 'performance', label: 'LLM Usage', icon: <FileText className="h-5 w-5" />, path: '/admin/performance' },
  { id: 'billing', label: 'LLM Costs', icon: <CreditCard className="h-5 w-5" />, path: '/admin/billing' },
];

export function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <img src="/logo.svg" alt="Mazo" className="w-8 h-8" />
            <div>
              <div className="font-semibold text-gray-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Mazo
              </div>
              <div className="text-xs text-purple-600 font-medium">Admin Panel</div>
            </div>
          </div>

          {/* Back to App Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to App
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors text-sm font-medium ${
                isActive(item.path)
                  ? 'bg-purple-50 text-purple-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <div className="text-xs text-gray-400" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Admin Mode
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

export default AdminLayout;
